// Settings → Approvals (CR_20260919 D3). The company default used to live in the header of the Requests page, and
// per-project approvers at the bottom of the project dialog — a company policy parked on a list page, and a routing
// list nobody would think to look for. Both are here now, beside the other company settings, with the per-project
// override (D1) and the cover share (D6 safeguard 2) that says whether the approver lists are the right names.
import {
  Alert,
  Autocomplete,
  Box,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { ProjectApproval } from '@fleetspoke/core/requests';
import { useTranslation } from 'react-i18next';
import { canManage, useSession } from '../../auth/session';
import { Loading, LoadError, Page } from '../../components/Page';
import { useProjects } from '../../data/billing';
import {
  useApprovalCoverStats,
  useCompanyManagers,
  useProjectApprovals,
  useProjectApprovers,
  useRequestApprovalRequired,
  useSetProjectApproval,
  useSetProjectApprovers,
  useSetRequestApproval,
} from '../../data/requests';

const OPTIONS: { value: ProjectApproval; key: string }[] = [
  { value: null, key: 'projects.approval_default' },
  { value: 'required', key: 'projects.approval_required' },
  { value: 'none', key: 'projects.approval_none' },
];

/** A project's named approvers, saved on every change. Empty reads "any manager" — the same fallback
 *  `app.request_approvers()` uses. Hidden when the project needs no approval (a list that decides nothing). */
function Approvers({ project, none }: { project: { id: string; name: string }; none: boolean }) {
  const { t } = useTranslation();
  const approvers = useProjectApprovers(project.id, !none);
  const managers = useCompanyManagers(!none);
  const setApprovers = useSetProjectApprovers();
  if (none)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  const ids = approvers.data ?? [];
  return (
    <Autocomplete
      multiple
      size="small"
      options={(managers.data ?? []).map((m) => m.user_id)}
      value={ids}
      disabled={setApprovers.isPending || approvers.isLoading}
      onChange={(_, v) => setApprovers.mutate({ projectId: project.id, userIds: v })}
      getOptionLabel={(id) => managers.data?.find((m) => m.user_id === id)?.name ?? '—'}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={ids.length ? undefined : t('settings.approvals.any_manager')}
          error={!!setApprovers.error}
          helperText={setApprovers.error ? (setApprovers.error as Error).message : undefined}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              'aria-label': t('settings.approvals.approvers_label', { project: project.name }),
            },
          }}
        />
      )}
      sx={{ minWidth: 240 }}
      data-testid={`approvers-${project.id}`}
    />
  );
}

export function ApprovalsPage() {
  const { t } = useTranslation();
  const { me } = useSession();
  const manager = canManage(me);
  const companyId = me?.company?.id;
  const required = useRequestApprovalRequired(companyId);
  const setRequired = useSetRequestApproval();
  const projects = useProjects();
  const approvals = useProjectApprovals(manager);
  const setApproval = useSetProjectApproval();
  const cover = useApprovalCoverStats(30, manager);

  if (!manager)
    return (
      <Page title={t('settings.approvals.title')}>
        <Alert severity="info">{t('counts.not_allowed')}</Alert>
      </Page>
    );

  const active = (projects.data ?? []).filter(
    (p) => p.status === 'active' || p.status === 'planned',
  );
  const error = projects.error ?? approvals.error;

  return (
    <Page title={t('settings.approvals.title')}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={required.data ?? true}
              disabled={setRequired.isPending || required.data === undefined}
              onChange={(e) => setRequired.mutate(e.target.checked)}
              data-testid="approval-required"
            />
          }
          label={
            <Box>
              <Typography variant="body2">{t('settings.approvals.company')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('settings.approvals.company_help')}
              </Typography>
            </Box>
          }
        />
      </Paper>

      <Typography variant="h6">{t('settings.approvals.projects')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('settings.approvals.projects_help')} {t('settings.approvals.autosave')}
      </Typography>

      {projects.isLoading ? <Loading /> : null}
      {error ? <LoadError error={error} /> : null}
      {projects.data && !active.length ? (
        <Typography color="text.secondary">{t('settings.approvals.no_projects')}</Typography>
      ) : null}

      {active.length ? (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" data-testid="project-approvals">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.approvals.col_project')}</TableCell>
                <TableCell>{t('settings.approvals.col_approval')}</TableCell>
                <TableCell>{t('settings.approvals.col_approvers')}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>{t('settings.approvals.col_cover')}</span>
                    <Tooltip title={t('settings.approvals.cover_help')}>
                      <HelpOutlineIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {active.map((p) => {
                const value = approvals.data?.get(p.id) ?? null;
                const stat = cover.data?.get(p.id);
                return (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={value ?? ''}
                        disabled={setApproval.isPending}
                        onChange={(e) =>
                          setApproval.mutate({
                            projectId: p.id,
                            approval: (e.target.value || null) as ProjectApproval,
                          })
                        }
                        slotProps={{
                          htmlInput: { 'aria-label': `${p.name} ${t('projects.approval')}` },
                          select: { displayEmpty: true },
                        }}
                        sx={{ minWidth: 170 }}
                      >
                        {OPTIONS.map((o) => (
                          <MenuItem key={o.key} value={o.value ?? ''}>
                            {o.value === null && required.data !== undefined
                              ? t('projects.approval_default_is', {
                                  value: t(
                                    required.data
                                      ? 'projects.approval_required'
                                      : 'projects.approval_none',
                                  ),
                                })
                              : t(o.key)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <Approvers project={p} none={value === 'none'} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={stat?.cover ? 'warning.main' : 'text.secondary'}
                      >
                        {stat?.approvals
                          ? t('settings.approvals.cover_share', {
                              cover: stat.cover,
                              approvals: stat.approvals,
                            })
                          : t('settings.approvals.cover_none')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
      {setApproval.error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(setApproval.error as Error).message}
        </Alert>
      ) : null}
    </Page>
  );
}
